<template>
  <div class="top-container">
    <div class="patient-summary">
      <div class="descriptor-container">
        <span class="descriptor-label">{{ tr('MRN') }}</span>
        <span class="datum">{{ patientData.mrn || '—' }}</span>
      </div>
      <div class="age-sex">
        <div class="descriptor-container">
          <span class="descriptor-label age">{{ tr(ageLabel) }}</span>
          <span class="datum">{{ patientData.age >= 0 ? patientData.age : '—' }}</span>
        </div>
        <div class="descriptor-container">
          <span class="descriptor-label">{{ tr('Sex') }}</span>
          <span class="datum">{{ patientData.sex || '—' }}</span>
        </div>
      </div>
      <div class="descriptor-container">
        <span class="descriptor-label">{{ tr('Mortality Status') }}</span>
        <span class="datum">{{ patientData.mortalityStatus || '—' }}</span>
      </div>
      <div class="descriptor-container">
        <span class="descriptor-label">{{ tr('Primary Diagnosis') }}</span>
        <span class="datum">{{ patientData.diagnosis || '—' }}</span>
      </div>
      <div class="descriptor-container">
        <span class="descriptor-label">{{ tr('Staging') }}</span>
        <span class="datum">{{ patientData.staging || '—' }}</span>
      </div>
    </div>
    <div class="add-button">
      <span class="material-symbols-outlined add-icon">add</span>
      <span class="add-text">{{ tr('Tumor Board') }}</span>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, inject, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { getRecord } from './patientEhr.js'

const tr = inject('tr')
const store = useStore()
const ageLabel = ref('Age')

const patientData = reactive({
  firstName: '',
  lastName: '',
  sex: '',
  mrn: '',
  age: -1,
  diagnosis: '',
  staging: '',
  mortalityStatus: '',
})

const patient = computed(() => store.getters.session.patient)
const genomicsResults = computed(() => store.getters.session?.genomicsResults || {})

const loadPatientData = async () => {
  patientData.firstName = patient.value.given || ''
  patientData.lastName = patient.value.family || ''
  patientData.sex = patient.value.gender || ''
  patientData.mrn = (patient.value.id?.length > 0 ? patient.value.id.split(',') : [''])[0]
  patientData.age = patient.value.age || -1
  patientData.mortalityStatus = patient.value.mortalityStatus || ''
  ageLabel.value = patientData.mortalityStatus === 'Deceased' ? 'Age at Time of Death' : 'Age'

  const record = await getRecord(patientData.mrn)
  console.log('tumorboard record', record)

  if (!record?.Upcoming?.length && !record?.Past?.length) {
    patientData.diagnosis = ''
    patientData.staging = ''
  } else {
    // Diagnosis: prefer Upcoming session for diagnosis
    // get earliest upcoming session else most recent past session
    const recordForDiagnosis = (record?.Upcoming.length ?
      record.Upcoming.toSorted(compareDatesAsc) : record.Past.toSorted(compareDatesDesc))[0]
    patientData.diagnosis = recordForDiagnosis.diagnosis || ''

    // Staging: we only see staging in past sessions
    // so use the most recent past record
    if (!record?.Past?.length) {
      patientData.staging = ''
      return
    }
    const mostRecentPastRecord = record.Past.toSorted(compareDatesDesc)[0]
    patientData.staging = mostRecentPastRecord.recording_info?.clinical_staging ?
      Object.keys(mostRecentPastRecord.recording_info.clinical_staging)[0].split('Stage ')[1] : ''
  }

  const cardState = {
    sampleId: genomicsResults.value.sample || '',
    diagnosis: patientData.diagnosis,
    selectedAlteration: genomicsResults.value.selectedAlteration || '',
    treatmentOrTrialType: genomicsResults.value.treatmentOrTrialType || '',
    treatmentOrTrialCount: genomicsResults.value.treatmentOrTrialCount || '',
  }
  store.dispatch('setGenomicsResults', { genomicsResults: cardState, publish: true })
}

// sort by date descending (latest to earliest)
const compareDatesDesc = (a, b) => {
  if (a.date < b.date) {
    return 1
  }
  if (a.date > b.date) {
    return -1
  }
  return 0
}

// sort by date ascending (earliest to latest)
const compareDatesAsc = (a, b) => {
  if (a.date < b.date) {
    return -1
  }
  if (a.date > b.date) {
    return 1
  }
  return 0
}

watch(patient, () => {
  loadPatientData()
})

await loadPatientData()
</script>

<style lang="scss" scoped>
.top-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 80%;
}

.add-button {
  cursor: pointer;
  display: flex;
  text-align: left;
  flex-direction: row;
  margin-top: 15px;
  margin-left: 10px;
  width: 35%;
  margin-bottom: 1px;
  border-radius: 5px;
  background-color: var(--Grey100);
  color: var(--Grey600);
  padding: 1%;
  transition: all linear 200ms;
  align-items: center;
  justify-content: center;

  &>.add-text {
    font-size: calc(5px + 0.3vw);
    transition: all linear 200ms;
    vertical-align: middle;

    &:hover {
      color: var(--Black);
    }
  }

  &>.add-icon {
    font-size: 140%;

    &:hover {
      color: var(--Grey800);
    }
  }
}

.patient-summary {
  display: flex;
  width: 100%;
  height: 85%;
  flex-direction: column;
  text-align: left;
  font-size: calc(7px + 0.2vw);
  overflow: auto;
}

.descriptor-container {
  margin: 8px 15px 0;
  width: 100%;
  display: block;
  align-content: left;
}

.datum {
  font-weight: 500;
}

.age-sex {
  display: flex;
}

.age {
  margin-right: 5.3vmax;
  width: 100%;
}

.descriptor-label {
  color: var(--Grey500);
  display: block;
  align-content: left;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.38px;
}
</style>
